"use client";

import { useState } from "react";

import { CardPhoto } from "@/components/ui/card-photo";
import FilterBar from "@/components/FilterBar";
import PortfolioModal from "@/components/PortfolioModal";

import { PortfolioItem } from "@/data/portfolio";
import type { FilterGroup } from "@/data/filters";
import { defaultSortConfigs, sortItems } from "@/lib/sortUtils";
import { track } from "@/utils/tracking";

type PortfolioClientProps = {
  initialPortfolio: PortfolioItem[];
  portfolioFilters: FilterGroup[];
  locale: string;
};

export default function PortfolioClient({ 
  initialPortfolio, 
  portfolioFilters
}: PortfolioClientProps) {
  
  // Initialize state for selected item and modal open state
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Initialize filtered items state
  const [filteredPortfolio, setFilteredPortfolio] = useState<PortfolioItem[]>(initialPortfolio);
  
  // Handle filter changes
  const handleFilterChange = (activeFilters: Record<string, string | null>) => {
    const hasActiveFilters = Object.values(activeFilters).some(filter => filter !== null);
    
    if (!hasActiveFilters) {
      // No filters active, show initial portfolio
      setFilteredPortfolio(initialPortfolio);
    } else {
      // Filters active, show filtered content
      // Import portfolio data dynamically for filtering
      import('@/data/portfolio').then(({ portfolio }) => {
        const filtered = portfolio.filter(item => {
          return Object.values(activeFilters).every(activeFilter => {
            if (activeFilter === null) return true;
            return item.tags.includes(activeFilter);
          });
        });
        
        setFilteredPortfolio(sortItems(filtered, defaultSortConfigs.portfolio));
      });
    }
  };

  // Handle card click
  const handleCardClick = (item: PortfolioItem) => {
    track('portfolio_item_clicked', {
      title: item.name
    });
    setSelectedItem(item);
    setModalOpen(true);
  };

  return (
    <>
      {/* Only show filter bar if there are filter groups configured */}
      {portfolioFilters.length > 0 && (
        <FilterBar 
          filterGroups={portfolioFilters}
          onFilterChange={handleFilterChange}
          className="mb-6"
        />
      )}
      
      {/* Always render cards client-side with proper event handlers */}
      <ul className="grid-template-logos mt-6 mb-2">
        {filteredPortfolio.map((item, index) => (
          <li key={`${item.name}-${index}`}>
            <CardPhoto
              className="cursor-pointer"
              photo={item.image}
              photoDark={item.imageDark}
              alt={item.name}
              onClick={() => handleCardClick(item)}
              tags={item.tags}
              gradientBorder
            />
          </li>
        ))}
      </ul>

      {/* Modal */}
      {selectedItem && (
        <PortfolioModal 
          item={selectedItem} 
          itemIndex={filteredPortfolio.indexOf(selectedItem)}
          open={modalOpen} 
          onOpenChange={setModalOpen}
        />
      )}
    </>
  );
}